import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { updateJobApplicationState, ApplicationState, type Job } from '../redux/features/jobs/jobsSlice';
import { useToast } from '../contexts/ToastContext';

interface ApplicationStateSelectorProps {
  jobId: string;
  currentState?: ApplicationState;
  onStateChange?: (newState: ApplicationState) => void; // Optional callback when state changes
  markAsAppliedOnStateSet?: boolean; // If true, will mark job as applied when a mutually exclusive state is set
  jobData?: { id: string; objectID: string; normalisedTitle?: string; title?: string; companyId?: string }; // Job data needed for marking as applied
  setAppliedJobState?: (job: any, applied: boolean) => Promise<boolean>; // Function to mark job as applied
  syncApplicationStateToBackend?: (jobId: string, applicationState?: ApplicationState, job?: Job) => Promise<boolean>; // Sync applicationState to backend
  isApplied?: boolean; // Whether job is already applied
  trackJobApply?: (data: { jobId: string; jobTitle: string; companyId: string; locale: string }) => void; // Analytics tracking
  locale?: string; // Locale for tracking
}

// Mutually exclusive application states (only one can be active at a time)
// Note: 'applied' and 'hidden' are independent flags tracked separately, not part of ApplicationState
const APPLICATION_STATES: Array<{ value: ApplicationState; labelKey: string; defaultLabel: string; color: string }> = [
  { value: null, labelKey: 'tools.applicationState.none', defaultLabel: 'None', color: 'gray' },
  { value: 'interviewing', labelKey: 'tools.applicationState.interviewing', defaultLabel: 'Interviewing', color: 'purple' },
  { value: 'offer', labelKey: 'tools.applicationState.offer', defaultLabel: 'Offer', color: 'green' },
  { value: 'rejected', labelKey: 'tools.applicationState.rejected', defaultLabel: 'Rejected', color: 'red' },
  { value: 'hired', labelKey: 'tools.applicationState.hired', defaultLabel: 'Hired', color: 'emerald' },
];

export const ApplicationStateSelector: React.FC<ApplicationStateSelectorProps> = ({ 
  jobId, 
  currentState,
  onStateChange,
  markAsAppliedOnStateSet = false,
  jobData,
  setAppliedJobState,
  syncApplicationStateToBackend,
  isApplied = false,
  trackJobApply,
  locale
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  
  // Check if job exists in any array (saved, viewed, or applied) to determine if we should sync
  const savedJobs = useAppSelector((state) => state.jobs.savedJobs);
  const viewedJobs = useAppSelector((state) => state.jobs.viewedJobs);
  const appliedJobs = useAppSelector((state) => state.jobs.appliedJobs);
  
  const isSaved = savedJobs.some((job) => job.id === jobId);
  const isViewed = viewedJobs.some((job) => job.id === jobId);
  const isAppliedFromRedux = appliedJobs.some((job) => job.id === jobId);
  const jobExistsInAnyArray = isSaved || isViewed || isAppliedFromRedux || isApplied;
  
  let showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number) => void = () => {};
  try {
    const toastContext = useToast();
    showToast = toastContext.showToast;
  } catch {
    // Toast context not available
  }

  const currentStateConfig = APPLICATION_STATES.find(s => s.value === currentState) || APPLICATION_STATES[0];
  // Align with static: when applied but no exclusive state, show blue (same as state button)
  const displayColor = currentState ? currentStateConfig.color : (isApplied || isAppliedFromRedux ? 'blue' : 'gray');

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value === '' ? null : e.target.value as ApplicationState;
    dispatch(updateJobApplicationState({ jobId, applicationState: newState }));
    
    // If setting a mutually exclusive state (not null) and markAsAppliedOnStateSet is true, also mark as applied
    let appliedSuccess = true;
    if (markAsAppliedOnStateSet && newState !== null && !isApplied && !isAppliedFromRedux && jobData && setAppliedJobState) {
      const payload = { ...jobData, id: jobData.id || jobData.objectID };
      appliedSuccess = await setAppliedJobState(payload, true);
      if (appliedSuccess && trackJobApply && locale) {
        trackJobApply({
          jobId: jobData.objectID,
          jobTitle: jobData.normalisedTitle || jobData.title || '',
          companyId: jobData.companyId || '',
          locale: locale,
        });
      }
    }
    
    // Always sync applicationState to backend if job exists in any array (saved, viewed, or applied)
    // Pass the applicationState directly to avoid stale closure issues
    // This ensures applicationState is persisted to DynamoDB just like saved/viewed/applied jobs
    let syncSuccess = true;
    if (jobExistsInAnyArray && syncApplicationStateToBackend) {
      syncSuccess = await syncApplicationStateToBackend(jobId, newState);
    }
    
    // Only show toast if both operations succeeded (or if sync wasn't needed)
    if (appliedSuccess && syncSuccess) {
      // Call optional callback
      if (onStateChange) {
        onStateChange(newState);
      }
      
      const stateConfig = APPLICATION_STATES.find(s => s.value === newState);
      if (stateConfig) {
        showToast(
          intl.formatMessage(
            { id: 'tools.applicationState.updated', defaultMessage: 'State updated to {state}' },
            { state: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }
          ),
          'success'
        );
      }
    } else if (onStateChange) {
      // Still call callback even if sync failed
      onStateChange(newState);
    }
  };

  return (
    <div className="flex items-center gap-2 py-1.5">
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
        <FormattedMessage id="tools.applicationState.label" defaultMessage="State:" description="Label for the application state selector dropdown (browsed, saved, applied, etc.)" />
      </label>
      <select
        value={currentState || ''}
        onChange={handleChange}
        className={`
          text-xs font-medium px-2.5 py-1.5 rounded border transition-all duration-200
          appearance-none bg-white dark:bg-gray-800
          border-gray-300 dark:border-gray-600
          text-gray-700 dark:text-gray-200
          hover:border-gray-400 dark:hover:border-gray-500
          focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400
          cursor-pointer
          ${displayColor === 'blue' ? 'text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600' : ''}
          ${displayColor === 'purple' ? 'text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-600' : ''}
          ${displayColor === 'green' ? 'text-green-700 dark:text-green-300 border-green-300 dark:border-green-600' : ''}
          ${displayColor === 'red' ? 'text-red-700 dark:text-red-300 border-red-300 dark:border-red-600' : ''}
          ${displayColor === 'emerald' ? 'text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600' : ''}
        `}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.25rem center',
          backgroundSize: '1em 1em',
          paddingRight: '1.75rem',
        }}
      >
        {APPLICATION_STATES.map((state) => (
          <option key={state.value || 'none'} value={state.value || ''}>
            <FormattedMessage id={state.labelKey} defaultMessage={state.defaultLabel} description="Application state option in the dropdown selector" />
          </option>
        ))}
      </select>
    </div>
  );
};

